import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Form, Input, Result, Spin, Typography, message, notification } from "antd";
import type { InputRef } from "antd";
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from "@ant-design/icons";
import type {
  LandingBlock,
  LandingDocument,
  LandingFormBlock,
  LandingFormField,
  LandingLeadPayload,
  LandingPageDTO,
  LandingBackgroundBlock,
  LandingVideoBlock,
} from "../../services/landingPages";
import {
  createEmptyLandingDocument,
  fetchPublicLanding,
  submitLandingLead,
  checkLandingLeadEmailAvailability,
  LANDING_DEFAULT_FONT_FAMILY,
} from "../../services/landingPages";
import type { Rule } from "antd/es/form";
import "./LandingPublic.css";
import dayjs from "dayjs";
const { Title, Paragraph, Text } = Typography;

const RESERVED_DEFAULT_SUCCESS = "Grazie! Ti ricontatteremo al più presto.";

type EmailStatus = "idle" | "checking" | "available" | "invalid" | "taken" | "error";

type EmailStatusState = {
  value: string;
  status: EmailStatus;
  message?: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const EMAIL_INVALID_MESSAGE = "Email non valida";
const EMAIL_TAKEN_MESSAGE = "Email già usata per questa promozione";
const EMAIL_CHECK_ERROR_MESSAGE = "Verifica email non riuscita";

type LandingPublicProps = { slug: string };
type PositionedBlock = LandingBlock & { style: React.CSSProperties };
type LandingFormValues = LandingLeadPayload & { privacyConsent?: boolean };

const DEFAULT_BLOCK_HEIGHT: Record<string, number> = {
  heading: 80,
  paragraph: 120,
  image: 180,
  video: 200,
  form: 360,
  background: 220,
};


const FOOTER_EXTRA_SPACE = 96;

/* ---------- overlay & background ---------- */
const clamp01 = (value?: number | string | null) => {
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isNaN(parsed)) return 0;
    return clamp01(parsed);
  }
  if (typeof value !== "number" || Number.isNaN(value)) return 0;
  const normalized = value > 1 ? value / 100 : value;
  return Math.min(Math.max(normalized, 0), 1);
};

const resolveOverlayColor = (color: string | null | undefined, opacity: number) => {
  const clamped = clamp01(opacity);
  if (clamped <= 0) return "rgba(0,0,0,0)";
  if (!color) return `rgba(15, 23, 42, ${clamped})`;

  const value = color.trim();
  const hexMatch = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(value);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split("").map((c) => `${c}${c}`).join("");
    const int = parseInt(hex, 16);
    const r = (int >> 16) & 255, g = (int >> 8) & 255, b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${clamped})`;
  }
  if (value.startsWith("rgba")) return value.replace(/rgba\(([^,]+),([^,]+),([^,]+),[^\)]+\)/, `rgba($1,$2,$3,${clamped})`);
  if (value.startsWith("rgb")) return value.replace(/rgb\(([^,]+),([^,]+),([^\)]+)\)/, `rgba($1,$2,$3,${clamped})`);
  return `rgba(15, 23, 42, ${clamped})`;
};

const composeCanvasBackground = (
  baseColor: string | null | undefined,
  imageUrl: string | null | undefined,
  overlayColorRaw: string | null | undefined,
  overlayOpacityRaw: number | string | null | undefined,
  fallbackColor: string,
): React.CSSProperties => {
  const overlayOpacity = clamp01(overlayOpacityRaw);
  const overlayColor = resolveOverlayColor(overlayColorRaw, overlayOpacity);
  const trimmedImage = imageUrl?.trim() ?? "";
  const trimmedColor = baseColor?.trim() ?? "";
  const style: React.CSSProperties = {};

  if (trimmedImage) {
    if (overlayOpacity > 0) {
      style.backgroundImage = `linear-gradient(${overlayColor}, ${overlayColor}), url(${trimmedImage})`;
      style.backgroundSize = "cover, cover";
      style.backgroundPosition = "center, center";
      style.backgroundRepeat = "no-repeat, no-repeat";
    } else {
      style.backgroundImage = `url(${trimmedImage})`;
      style.backgroundSize = "cover";
      style.backgroundPosition = "center";
      style.backgroundRepeat = "no-repeat";
    }
    style.backgroundColor = trimmedColor || fallbackColor;
    return style;
  }

  if (trimmedColor) {
    if (/gradient/i.test(trimmedColor)) {
      style.backgroundImage = trimmedColor;
      style.backgroundSize = "cover";
      style.backgroundRepeat = "no-repeat";
      style.backgroundPosition = "center";
      return style;
    }
    style.backgroundColor = trimmedColor;
    return style;
  }

  style.backgroundColor = fallbackColor;
  return style;
};

type VideoEmbedConfig =
  | { type: "iframe"; src: string }
  | { type: "video"; src: string };

const resolveVideoEmbedConfig = (url?: string | null): VideoEmbedConfig | null => {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const youtubeMatch =
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/.exec(trimmed);
  if (youtubeMatch) {
    return { type: "iframe", src: `https://www.youtube.com/embed/${youtubeMatch[1]}` };
  }

  const vimeoMatch = /vimeo\.com\/(\d+)/.exec(trimmed);
  if (vimeoMatch) {
    return { type: "iframe", src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
  }

  if (/\.(mp4|webm|ogg)(?:\?.*)?$/i.test(trimmed)) {
    return { type: "video", src: trimmed };
  }

  return { type: "iframe", src: trimmed };
};

const mapFieldId = (id: string): keyof LandingLeadPayload | null => {
  switch (id) {
    case "firstName": return "firstName";
    case "lastName": return "lastName";
    case "email": return "email";
    case "phoneNumber": return "phoneNumber";
    default: return null;
  }
};

/* ---------- stage size: allineato al builder ---------- */
const STAGE_WIDTH = 430; // builder default: 430px :contentReference[oaicite:5]{index=5}
const STAGE_HEIGHT = 932;

/* ---------- prepareDocument: preserva overlay ---------- */
const prepareDocument = (doc?: LandingDocument | null): LandingDocument => {
  const base = doc ?? createEmptyLandingDocument();
  const size = {
    width: base.size?.width ?? STAGE_WIDTH,
    height: base.size?.height ?? STAGE_HEIGHT,
  };
  const allowedTypes = new Set(["heading", "paragraph", "image", "video", "form", "background"]);
  const blocks = (Array.isArray(base.blocks) ? base.blocks : [])
    .map((block) => {
      if (!block) return null;
      const rawType = (block as { type?: string }).type;
      const normalizedType = typeof rawType === "string" ? rawType.toLowerCase() : "";
      if (!allowedTypes.has(normalizedType)) {
        return null;
      }
      const normalizedBlock = { ...block, type: normalizedType } as LandingBlock;

      if (normalizedType === "form") {
        const formBlock = normalizedBlock as LandingFormBlock;
        return {
          ...formBlock,
          fields: (formBlock.fields ?? [])
            .filter((field) => field?.id !== "notes")
            .map((field) => ({ ...field, visible: field?.visible !== false })),
          background: formBlock.background ?? "#ffffff",
          textColor: formBlock.textColor ?? "#0f172a",
          buttonColor: formBlock.buttonColor ?? "#007AFF",
        } as LandingFormBlock;
      }
      if (normalizedType === "video") {
        const videoBlock = normalizedBlock as LandingVideoBlock;
        return {
          ...videoBlock,
          url: videoBlock.url ?? "",
          posterUrl: videoBlock.posterUrl ?? null,
          autoplay: videoBlock.autoplay ?? false,
          muted: videoBlock.muted !== false,
          loop: videoBlock.loop ?? false,
          showControls: videoBlock.showControls !== false,
          size: videoBlock.size ?? {
            width: 320,
            height: DEFAULT_BLOCK_HEIGHT.video,
          },
        } as LandingVideoBlock;
      }
      if (normalizedType === "heading" || normalizedType === "paragraph") {
        return { ...(normalizedBlock as LandingBlock), fontFamily: (normalizedBlock as LandingBlock & { fontFamily?: string }).fontFamily ?? "inherit" };
      }
      if (normalizedType === "background") {
        const backgroundBlock = normalizedBlock as LandingBackgroundBlock;
        return {
          ...backgroundBlock,
          color: backgroundBlock.color ?? "#e2e8f0",
          borderRadius: backgroundBlock.borderRadius ?? 0,
          size: backgroundBlock.size ?? {
            width: 360,
            height: DEFAULT_BLOCK_HEIGHT.background,
          },
        } as LandingBackgroundBlock;
      }
      return normalizedBlock;
    })
    .filter((block): block is LandingBlock => Boolean(block));

  const footer = {
    content: base.footer?.content?.trim() || `\u00A9 ${new Date().getFullYear()} La tua azienda. Tutti i diritti riservati.`,
    align: base.footer?.align ?? "center",
    textColor: base.footer?.textColor ?? "#475569",
    background: base.footer?.background ?? "#e2e8f0",
    fontSize: base.footer?.fontSize ?? 14,
    spacing: base.footer?.spacing ?? 12,
    fontFamily: base.footer?.fontFamily?.trim() || LANDING_DEFAULT_FONT_FAMILY,
  };

  return {
    viewport: "mobile",
    blocks,
    metadata: {
      ...base.metadata,
      defaultFontFamily: base.metadata?.defaultFontFamily?.trim() || LANDING_DEFAULT_FONT_FAMILY,
    },
    background: {
      color: base.background?.color ?? null,
      imageUrl: base.background?.imageUrl ?? null,
      overlayColor: (base as any).background?.overlayColor ?? null,
      overlayOpacity: (base as any).background?.overlayOpacity ?? null,
    } as LandingDocument["background"],
    size,
    footer,
  };
};

/* ---------- font loader: per corrispondenza 1:1 con il builder ---------- */
const GOOGLE_FONT_MAP: Record<string, string> = {
  Inter: "Inter:wght@400;600;700",
  Poppins: "Poppins:wght@400;600;700",
  Montserrat: "Montserrat:wght@400;600;700",
  "Space Grotesk": "Space+Grotesk:wght@400;600;700",
  Lora: "Lora:wght@400;600;700",
  "Playfair Display": "Playfair+Display:wght@400;600;700",
};

const normalizeFamilyName = (raw?: string) => (raw ?? "").split(",")[0].replace(/['"]/g, "").trim();

const ensurePreconnectOnce = () => {
  if (!document.getElementById("lp-font-preconnect-1")) {
    const l1 = document.createElement("link");
    l1.id = "lp-font-preconnect-1";
    l1.rel = "preconnect";
    l1.href = "https://fonts.googleapis.com";
    document.head.appendChild(l1);
  }
  if (!document.getElementById("lp-font-preconnect-2")) {
    const l2 = document.createElement("link");
    l2.id = "lp-font-preconnect-2";
    l2.rel = "preconnect";
    l2.href = "https://fonts.gstatic.com";
    l2.crossOrigin = "anonymous";
    document.head.appendChild(l2);
  }
};

const ensureGoogleFont = (familyKey: string) => {
  const id = `lp-font-${familyKey.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const href = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONT_MAP[familyKey]}&display=swap`;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
};

const useLoadPublicFonts = (doc: LandingDocument) => {
  useEffect(() => {
    const wanted = new Set<string>();
    const defaultFamily = normalizeFamilyName(doc.metadata?.defaultFontFamily);
    if (defaultFamily && GOOGLE_FONT_MAP[defaultFamily]) wanted.add(defaultFamily);

    const footerFamily = normalizeFamilyName(doc.footer?.fontFamily);
    if (footerFamily && GOOGLE_FONT_MAP[footerFamily]) wanted.add(footerFamily);

    for (const b of doc.blocks) {
      if (b.type === "heading" || b.type === "paragraph") {
        const family = b.fontFamily && b.fontFamily !== "inherit" ? normalizeFamilyName(b.fontFamily) : defaultFamily;
        if (family && GOOGLE_FONT_MAP[family]) wanted.add(family);
      }
    }

    if (wanted.size > 0) {
      ensurePreconnectOnce();
      wanted.forEach((f) => ensureGoogleFont(f));
    }
  }, [doc]);
};

/* ---------- COMPONENT ---------- */
const LandingPublic: React.FC<LandingPublicProps> = ({ slug }) => {
  const [loading, setLoading] = useState(true);
  const [landing, setLanding] = useState<LandingPageDTO | null>(null);
  const [landingDocument, setLandingDocument] = useState<LandingDocument>(createEmptyLandingDocument);
  const [error, setError] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatusState>({ value: "", status: "idle" });
  const [form] = Form.useForm<LandingFormValues>();
  const emailInputRef = useRef<InputRef | null>(null);
  const emailFocusIntentRef = useRef(false);
  const hasDocumentBlocks = landingDocument.blocks.length > 0;
  const eventDateDisplay = useMemo(() => {
    if (!landing?.eventDate) return null;
    const parsed = dayjs(landing.eventDate);
    if (!parsed.isValid()) return null;
    return parsed.format("DD/MM/YYYY HH:mm");
  }, [landing?.eventDate]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLanding(null);
    setFormSubmitting(false);
    setError(null);

    const load = async () => {
      try {
        const data = await fetchPublicLanding(slug);
        if (!active) return;
        setLanding(data);
        setLandingDocument(prepareDocument(data.document));
        if (data.name) window.document.title = data.name;
      } catch (err) {
        if (!active) return;
        const messageText = err instanceof Error ? err.message : "Pagina non trovata";
        setError(messageText);
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    return () => {
      if (successButtonTimerRef.current) {
        window.clearTimeout(successButtonTimerRef.current);
        successButtonTimerRef.current = null;
      }
    };
  }, []);

  /* carica i webfont eventualmente usati nel builder */
  useLoadPublicFonts(landingDocument);

  const emailValue = Form.useWatch("email", form);
  const emailCheckTimeoutRef = useRef<number | null>(null);
  const emailFetchIdRef = useRef(0);
  const emailBlurTimeoutRef = useRef<number | null>(null);
  const successButtonTimerRef = useRef<number | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const sanitizeLeadPayload = useCallback((payload: LandingLeadPayload): LandingLeadPayload => {
    const normalize = (value?: string | null) => {
      if (typeof value !== "string") {
        return undefined;
      }
      const trimmed = value.trim();
      return trimmed ? trimmed : undefined;
    };

    const email = (payload.email ?? "").trim();
    return {
      email,
      firstName: normalize(payload.firstName),
      lastName: normalize(payload.lastName),
      phoneNumber: normalize(payload.phoneNumber),
    };
  }, []);

  const emailRules = useMemo<Rule[]>(() => [{ required: true, message: "Inserisci la email" }], []);

  useEffect(() => {
    if (emailCheckTimeoutRef.current !== null) {
      window.clearTimeout(emailCheckTimeoutRef.current);
      emailCheckTimeoutRef.current = null;
    }
    emailFetchIdRef.current += 1;
    setEmailStatus({ value: "", status: "idle" });
    form.resetFields();
  }, [form, slug]);

  useEffect(() => {
    if (emailCheckTimeoutRef.current !== null) {
      window.clearTimeout(emailCheckTimeoutRef.current);
      emailCheckTimeoutRef.current = null;
    }

    const raw = typeof emailValue === "string" ? emailValue : "";
    const trimmed = raw.trim();

    if (!trimmed) {
      emailFetchIdRef.current += 1;
      setEmailStatus({ value: "", status: "idle" });
      return;
    }

    if (!EMAIL_PATTERN.test(trimmed)) {
      emailFetchIdRef.current += 1;
      setEmailStatus({ value: trimmed, status: "invalid", message: EMAIL_INVALID_MESSAGE });
      return;
    }

    setEmailStatus({ value: trimmed, status: "checking" });

    const fetchId = emailFetchIdRef.current + 1;
    emailFetchIdRef.current = fetchId;

    emailCheckTimeoutRef.current = window.setTimeout(() => {
      checkLandingLeadEmailAvailability(slug, trimmed)
        .then((result) => {
          if (emailFetchIdRef.current !== fetchId) {
            return;
          }
          if (result.available) {
            setEmailStatus({ value: trimmed, status: "available" });
          } else {
            setEmailStatus({ value: trimmed, status: "taken", message: EMAIL_TAKEN_MESSAGE });
          }
        })
        .catch((err) => {
          if (emailFetchIdRef.current !== fetchId) {
            return;
          }
          const messageText = err instanceof Error ? err.message : EMAIL_CHECK_ERROR_MESSAGE;
          setEmailStatus({ value: trimmed, status: "error", message: messageText });
        });
    }, 300);

    return () => {
      if (emailCheckTimeoutRef.current !== null) {
        window.clearTimeout(emailCheckTimeoutRef.current);
        emailCheckTimeoutRef.current = null;
      }
      emailFetchIdRef.current += 1;
    };
  }, [emailValue, form, slug]);

  const onFormValuesChange = useCallback(
    (_changedValues: Partial<LandingFormValues>, _values?: LandingFormValues) => {
      // no-op: kept for potential future side-effects (i.e. live validation)
    },
    [],
  );

  const handleEmailFocus = useCallback(() => {
    if (emailBlurTimeoutRef.current !== null) {
      window.clearTimeout(emailBlurTimeoutRef.current);
      emailBlurTimeoutRef.current = null;
    }
    emailFocusIntentRef.current = true;
  }, []);

  const handleEmailBlur = useCallback(() => {
    if (emailBlurTimeoutRef.current !== null) {
      window.clearTimeout(emailBlurTimeoutRef.current);
    }
    emailBlurTimeoutRef.current = window.setTimeout(() => {
      emailFocusIntentRef.current = false;
      emailBlurTimeoutRef.current = null;
    }, 150);
  }, []);

  useEffect(() => {
    if (!emailFocusIntentRef.current) {
      return;
    }
    const inputElement = emailInputRef.current?.input;
    if (!inputElement) {
      return;
    }
    const raw = typeof emailValue === "string" ? emailValue : "";
    if (!raw) {
      return;
    }
    if (document.activeElement !== inputElement) {
      inputElement.focus({ preventScroll: true });
      const valueLength = inputElement.value.length;
      try {
        inputElement.setSelectionRange(valueLength, valueLength);
      } catch {
        // ignore if not supported
      }
    }
  }, [emailStatus.status, emailStatus.message, emailValue]);

  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current !== null) {
        window.clearTimeout(emailCheckTimeoutRef.current);
      }
      if (emailBlurTimeoutRef.current !== null) {
        window.clearTimeout(emailBlurTimeoutRef.current);
      }
    };
  }, []);

  const emailErrors = form.getFieldError("email");

  const emailCustomMessage = useMemo(() => {
    switch (emailStatus.status) {
      case "invalid":
        return EMAIL_INVALID_MESSAGE;
      case "taken":
        return EMAIL_TAKEN_MESSAGE;
      case "error":
        return emailStatus.message || EMAIL_CHECK_ERROR_MESSAGE;
      default:
        return undefined;
    }
  }, [emailStatus.message, emailStatus.status]);

  const emailHelp = emailErrors.length > 0 ? undefined : emailCustomMessage;

  const emailValidateStatus =
    emailErrors.length > 0
      ? "error"
      : emailStatus.status === "available"
      ? "success"
      : emailStatus.status === "checking"
      ? "validating"
      : emailCustomMessage
      ? "error"
      : undefined;

  const emailSuffix = useMemo<React.ReactNode>(() => {
    switch (emailStatus.status) {
      case "checking":
        return <LoadingOutlined className="landing-public__field-status landing-public__field-status--loading" />;
      case "available":
        return <CheckCircleFilled className="landing-public__field-status landing-public__field-status--available" />;
      case "invalid":
      case "taken":
      case "error":
        return <CloseCircleFilled className="landing-public__field-status landing-public__field-status--error" />;
      default:
        return null;
    }
  }, [emailStatus.status]);

  const defaultFontFamily = useMemo(
    () => landingDocument.metadata?.defaultFontFamily?.trim() || LANDING_DEFAULT_FONT_FAMILY,
    [landingDocument.metadata?.defaultFontFamily],
  );

  /* wrapper full viewport (variante fallback) */
  const backgroundStyle = useMemo(() => {
    const color = landingDocument.background?.color?.trim() || landing?.backgroundColor?.trim();
    const image = landingDocument.background?.imageUrl?.trim() || landing?.backgroundImageUrl?.trim();
    const overlayRaw = landing?.backgroundOverlayColor ?? landingDocument.background?.overlayColor ?? null;
    const overlayOpacity = landing?.backgroundOverlayOpacity ?? landingDocument.background?.overlayOpacity ?? null;

    const style: React.CSSProperties = {
      minHeight: "100vh",
      width: "100%",
      boxSizing: "border-box",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: hasDocumentBlocks ? "flex-start" : "center",
      padding: hasDocumentBlocks ? "32px 0 64px" : "40px 16px",
      fontFamily: defaultFontFamily,
    };

    Object.assign(style, composeCanvasBackground(color, image, overlayRaw, overlayOpacity, "#f7f9fb"));
    return style;
  }, [
    hasDocumentBlocks,
    defaultFontFamily,
    landingDocument.background?.color,
    landingDocument.background?.imageUrl,
    landingDocument.background?.overlayColor,
    landingDocument.background?.overlayOpacity,
    landing?.backgroundColor,
    landing?.backgroundImageUrl,
    landing?.backgroundOverlayColor,
    landing?.backgroundOverlayOpacity,
  ]);

  const positionedBlocks = useMemo<PositionedBlock[]>(() => {
    return landingDocument.blocks
      .map((block) => {
        const width = block.size?.width ?? (block.type === "form" ? 320 : 300);
        const defaultHeight = DEFAULT_BLOCK_HEIGHT[block.type as string] ?? 180;
        const height = block.size?.height ?? defaultHeight;
        const rotate = block.rotation ?? 0;
        const style: React.CSSProperties = {
          position: "absolute",
          left: block.position.x,
          top: block.position.y,
          width,
          zIndex: block.zIndex ?? 1,
          transform: rotate ? `rotate(${rotate}deg)` : undefined,
        };

        if (block.type === "form") {
          style.minHeight = height;
        } else {
          style.height = height;
        }

        return { ...block, style };
      })
      .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
  }, [landingDocument.blocks]);

  const stageWidth = landingDocument.size?.width ?? STAGE_WIDTH;
  const footerSpacing = landingDocument.footer?.spacing ?? 12;

  const stageHeight = useMemo(() => {
    const baseHeight = landingDocument.size?.height ?? STAGE_HEIGHT;
    const footerReserve = Math.max(footerSpacing ) + FOOTER_EXTRA_SPACE;

    if (!landingDocument.blocks.length) return baseHeight;

    const maxBottom = landingDocument.blocks.reduce((max, block) => {
      const fallback = DEFAULT_BLOCK_HEIGHT[block.type as string] ?? 120;
      const blockHeight = block.size?.height ?? fallback;
      return Math.max(max, block.position.y + blockHeight);
    }, 0);

    const finalHeight = Math.max(baseHeight, maxBottom + footerReserve);
    return Math.ceil(finalHeight);
  }, [landingDocument.blocks, landingDocument.size?.height, footerSpacing]);

  /* background per la variante custom (pagina/canvas) */
  const customBackgroundStyle = useMemo<React.CSSProperties>(() => {
    const color = landingDocument.background?.color?.trim() || landing?.backgroundColor?.trim();
    const image = landingDocument.background?.imageUrl?.trim() || landing?.backgroundImageUrl?.trim();
    const overlayRaw = landing?.backgroundOverlayColor ?? landingDocument.background?.overlayColor ?? null;
    const overlayOpacity = landing?.backgroundOverlayOpacity ?? landingDocument.background?.overlayOpacity ?? null;

    const style: React.CSSProperties = {
      minHeight: "100vh",
      width: "100%",
      boxSizing: "border-box",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "center",
      fontFamily: defaultFontFamily,
    };

    Object.assign(style, composeCanvasBackground(color, image, overlayRaw, overlayOpacity, "#f7f9fb"));
    return style;
  }, [
    defaultFontFamily,
    landing?.backgroundColor,
    landing?.backgroundImageUrl,
    landing?.backgroundOverlayColor,
    landing?.backgroundOverlayOpacity,
    landingDocument.background?.color,
    landingDocument.background?.imageUrl,
    landingDocument.background?.overlayColor,
    landingDocument.background?.overlayOpacity,
  ]);

  /* card pagina */
  const pageStyle = useMemo<React.CSSProperties>(() => {
    const color = landingDocument.background?.color?.trim() || landing?.backgroundColor?.trim();
    const image = landingDocument.background?.imageUrl?.trim() || landing?.backgroundImageUrl?.trim();
    const overlayRaw = landing?.backgroundOverlayColor ?? landingDocument.background?.overlayColor ?? null;
    const overlayOpacityRaw = landing?.backgroundOverlayOpacity ?? landingDocument.background?.overlayOpacity ?? null;

    const style: React.CSSProperties = {
      position: "relative",
      width: "100%",
      maxWidth: stageWidth,
      minHeight: stageHeight,
      margin: "0 auto",

      paddingBottom:  footerSpacing + FOOTER_EXTRA_SPACE,
 
      boxSizing: "border-box",
      fontFamily: defaultFontFamily,
      overflow: "hidden",
      borderRadius: "0px",
      boxShadow: "0 24px 48px rgba(15, 23, 42, 0.12)",
    };

    Object.assign(style, composeCanvasBackground(color, image, overlayRaw, overlayOpacityRaw, "#f1f5f9"));
    return style;
  }, [
    defaultFontFamily,
    footerSpacing,
    stageHeight,
    stageWidth,
    landing?.backgroundColor,
    landing?.backgroundImageUrl,
    landing?.backgroundOverlayColor,
    landing?.backgroundOverlayOpacity,
    landingDocument.background?.color,
    landingDocument.background?.imageUrl,
    landingDocument.background?.overlayColor,
    landingDocument.background?.overlayOpacity,
  ]);

  const primaryButtonStyle = useMemo(() => {
    const primary = landing?.primaryColor?.trim();
    if (!primary) return {};
    if (/gradient/i.test(primary)) return { backgroundImage: primary, border: "none", color: "#ffffff" };
    return { backgroundColor: primary, borderColor: primary, color: "#ffffff" };
  }, [landing?.primaryColor]);

  const onSubmit = async (values: LandingFormValues, successMessageOverride?: string) => {
    const { privacyConsent, ...payload } = values;
    const sanitized = sanitizeLeadPayload(payload);
    if (emailStatus.status === "checking") {
      message.info("Attendi il completamento della verifica email.");
      return;
    }
    if (emailStatus.status === "invalid") {
      message.error(EMAIL_INVALID_MESSAGE);
      return;
    }
    if (emailStatus.status === "taken") {
      message.error(EMAIL_TAKEN_MESSAGE);
      return;
    }
    if (emailStatus.status === "error") {
      message.error(emailStatus.message || EMAIL_CHECK_ERROR_MESSAGE);
      return;
    }

    setFormSubmitting(true);
    try {
      await submitLandingLead(slug, sanitized);
      const successMessage =
        successMessageOverride?.trim() || landing?.successMessage?.trim() || RESERVED_DEFAULT_SUCCESS;
      if (successMessage) {
        notification.success({
          message: "Coupon inviato!",
          description: successMessage,
          placement: "topRight",
        });
      }
      setSubmitSuccess(true);
      if (successButtonTimerRef.current) {
        window.clearTimeout(successButtonTimerRef.current);
      }
      successButtonTimerRef.current = window.setTimeout(() => {
        setSubmitSuccess(false);
        successButtonTimerRef.current = null;
      }, 3000);
      setEmailStatus({ value: "", status: "idle" });
      if (emailCheckTimeoutRef.current !== null) {
        window.clearTimeout(emailCheckTimeoutRef.current);
        emailCheckTimeoutRef.current = null;
      }
      emailFetchIdRef.current += 1;
      if (emailBlurTimeoutRef.current !== null) {
        window.clearTimeout(emailBlurTimeoutRef.current);
        emailBlurTimeoutRef.current = null;
      }
      emailFocusIntentRef.current = false;
      form.resetFields();
    } catch (err) {
      const messageText = err instanceof Error ? err.message : "Invio non riuscito";
      message.error(messageText);
      setSubmitSuccess(false);
    } finally {
      setFormSubmitting(false);
    }
  };

  const buildSubmitHandler = useCallback(
    (successMessageOverride?: string) => (values: LandingFormValues) =>
      onSubmit(values, successMessageOverride),
    [onSubmit],
  );

  const renderFormBlock = (block: LandingFormBlock) => {
    const fields =
      block.fields
        ?.filter(
          (field): field is LandingFormField =>
            Boolean(field?.id) && field.visible !== false && field.id !== "notes",
        ) ?? [];
    const buttonColorValue = block.buttonColor?.trim() || landing?.primaryColor?.trim() || "#007AFF";
    const buttonStyle = /gradient/i.test(buttonColorValue)
      ? { backgroundImage: buttonColorValue, border: "none", color: "#ffffff" }
      : { backgroundColor: buttonColorValue, borderColor: buttonColorValue, color: "#ffffff" };
    const successMessage = block.successMessage?.trim() || landing?.successMessage?.trim() || RESERVED_DEFAULT_SUCCESS;

    return (
      <div
        className="landing-public__form-block"
        style={{
          background: block.background ?? "#ffffff",
          color: block.textColor ?? "#0f172a",
          borderRadius: block.borderRadius ?? 16,
          fontFamily: defaultFontFamily,
        }}
      >
        {block.title ? <h3>{block.title}</h3> : null}
        {block.description ? <p>{block.description}</p> : null}

        <Form<LandingFormValues>
          form={form}
          layout="vertical"
          onFinish={buildSubmitHandler(successMessage)}
          onValuesChange={onFormValuesChange}
          requiredMark={false}
          className="landing-public__form-structure"
        >
          {fields.map((field) => {
            const payloadKey = mapFieldId(field.id);
            if (!payloadKey) return null;

            const placeholder = field.placeholder?.trim() || field.label || "";
            const ariaLabel = field.label?.trim() || placeholder || undefined;

            if (payloadKey === "email") {
              return (
                <Form.Item
                  key={field.id}
                  name={payloadKey}
                  rules={emailRules}
                  validateStatus={emailValidateStatus}
                  help={emailHelp}
                  validateTrigger={["onChange", "onBlur"]}
                  validateDebounce={300}
                >
                  <Input
                    type="email"
                    placeholder={placeholder}
                    autoComplete="email"
                    aria-label={ariaLabel}
                    ref={emailInputRef}
                    onFocus={handleEmailFocus}
                    onBlur={handleEmailBlur}
                    suffix={emailSuffix ?? undefined}
                  />
                </Form.Item>
              );
            }

            const rules: Rule[] = [];
            if (field.required) {
              const label = field.label?.toLowerCase?.() ?? "il valore";
              rules.push({ required: true, message: `Inserisci ${label}` });
            }

            return (
              <Form.Item key={field.id} name={payloadKey} rules={rules}>
                {field.type === "textarea" ? (
                  <Input.TextArea rows={3} placeholder={placeholder} aria-label={ariaLabel} />
                ) : (
                  <Input
                    type={field.type === "email" ? "email" : "text"}
                    placeholder={placeholder}
                    autoComplete={payloadKey}
                    aria-label={ariaLabel}
                  />
                )}
              </Form.Item>
            );
          })}

          <Form.Item
            name="privacyConsent"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error("Per proseguire è necessario accettare l'informativa privacy.")),
              },
            ]}
            className="landing-public__privacy-item"
          >
            <Checkbox className="landing-public__privacy-consent">
              <Text type="secondary">
                Accetto l'<a className="landing-public__privacy-link" href="/privacy" target="_blank" rel="noopener noreferrer">informativa privacy</a>
              </Text>
            </Checkbox>
          </Form.Item>
          <Form.Item className="landing-public__submit-item">
            <Button
              htmlType="submit"
              type="primary"
              block
              style={buttonStyle}
              disabled={formSubmitting}
              aria-busy={formSubmitting}
            >
              {formSubmitting ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <LoadingOutlined spin />
                  <span>Invio...</span>
                </span>
              ) : (
                block.buttonLabel?.trim() || landing?.buttonLabel?.trim() || "Invia"
              )}
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  };

  const renderBlockContent = (block: PositionedBlock) => {
    switch (block.type) {
      case "heading":
      case "paragraph": {
        const fontSize = block.fontSize ?? (block.type === "heading" ? 28 : 16);
        /* allineato al builder: default = 'regular' (non bold) */
        const weight = block.fontWeight ?? "regular"; // :contentReference[oaicite:6]{index=6}
        const lineHeight = block.lineHeight ?? (block.type === "heading" ? 1.2 : 1.6);
        const fontFamily = block.fontFamily && block.fontFamily !== "inherit" ? block.fontFamily : defaultFontFamily;
        return (
          <div
            className="landing-public__text"
            style={{
              textAlign: block.align ?? "left",
              fontSize,
              fontWeight: weight === "bold" ? 700 : weight === "medium" ? 600 : 400,
              color: block.color ?? "#111827",
              lineHeight,
              fontFamily,
              width: "100%",
            }}
          >
            {block.content}
          </div>
        );
      }
      case "background": {
        const backgroundBlock = block as LandingBackgroundBlock;
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: backgroundBlock.color ?? "#e2e8f0",
              borderRadius: backgroundBlock.borderRadius ?? 0,
            }}
          />
        );
      }
      case "image": {
        return (
          <img
            className="landing-public__image"
            src={block.url}
            alt={block.alt ?? "Immagine"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: block.objectFit ?? "cover",
              borderRadius: block.cornerRadius ?? 12,
              boxShadow: block.shadow ? "0 18px 40px rgba(15, 23, 42, 0.18)" : undefined,
            }}
          />
        );
      }
      case "video": {
        const videoBlock = block as LandingVideoBlock;
        const embed = resolveVideoEmbedConfig(videoBlock.url);
        if (!embed) {
          return (
            <div className="landing-public__video landing-public__video--placeholder">
              <div className="landing-public__video-message">
                <Text strong>Video non disponibile</Text>
                {videoBlock.url ? (
                  <Text type="secondary" ellipsis>
                    {videoBlock.url}
                  </Text>
                ) : null}
              </div>
            </div>
          );
        }

        const autoplay = Boolean(videoBlock.autoplay);
        const muted = videoBlock.muted !== false || autoplay;
        const loop = Boolean(videoBlock.loop);
        const controls = videoBlock.showControls !== false;

        if (embed.type === "iframe") {
          const separator = embed.src.includes("?") ? "&" : "?";
          const iframeSrc = autoplay
            ? `${embed.src}${separator}autoplay=1&mute=1&rel=0`
            : embed.src;
          return (
            <div className="landing-public__video">
              <iframe
                title={videoBlock.id ? `video-${videoBlock.id}` : "Video"}
                src={iframeSrc}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          );
        }

        return (
          <div className="landing-public__video">
            <video
              src={embed.src}
              poster={videoBlock.posterUrl ?? undefined}
              autoPlay={autoplay}
              muted={muted}
              loop={loop}
              controls={controls}
              playsInline
            />
          </div>
        );
      }
      case "form":
        return renderFormBlock(block);
      default:
        return null;
    }
  };

  const renderFooter = (options?: { flush?: boolean }) => {
    const footer = landingDocument.footer;
    const lines = footer.content.split(/\r?\n/);
    const baseStyle: React.CSSProperties = {
      textAlign: footer.align ?? "center",
      background: footer.background ?? "#e2e8f0",
      color: footer.textColor ?? "#475569",
      fontSize: footer.fontSize ?? 14,
      fontFamily: footer.fontFamily ?? defaultFontFamily,
      rowGap: footer.spacing ?? 12,
      width: "100%",
      margin: 0,
      padding: 0,
      borderRadius: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: footer.align === "left" ? "flex-start" : footer.align === "right" ? "flex-end" : "center",
    };

    if (options?.flush) {
      baseStyle.marginTop = Math.max(32, FOOTER_EXTRA_SPACE);
      baseStyle.alignSelf = "stretch";
      baseStyle.padding = `${footer.spacing ?? 12}px 0`;
    } else {
      baseStyle.position = "absolute";
      baseStyle.left = 0;
      baseStyle.right = 0;
      baseStyle.bottom = 0;
      baseStyle.padding = `${footer.spacing ?? 12}px 24px`;
    }
    return (
      <div className="landing-public__footer" style={baseStyle}>
        {lines.map((line, index) => (
          <span key={index}>{line || " "}</span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="landing-public__loading" style={backgroundStyle}>
        <Spin size="large" tip="Caricamento landing..." />
      </div>
    );
  }

  if (error || !landing) {
    return (
      <div className="landing-public__loading" style={backgroundStyle}>
        <Result status="404" title="Pagina non trovata" subTitle={error || "La landing richiesta non è disponibile."} />
      </div>
    );
  }

  const hasCustomDocument = positionedBlocks.length > 0;

  if (!hasCustomDocument) {
    const successMessage = landing.successMessage?.trim() || RESERVED_DEFAULT_SUCCESS;

    return (
      <div className="landing-public" style={backgroundStyle}>
        <div className="landing-public__card">
          <div className="landing-public__content">
            <div className="landing-public__texts">
              <Title level={2} className="landing-public__title">{landing.name}</Title>
              {eventDateDisplay ? (
                <Paragraph className="landing-public__event-date">
                  <Text strong>Data evento:</Text> {eventDateDisplay}
                </Paragraph>
              ) : null}
              {landing.description ? <Paragraph className="landing-public__description">{landing.description}</Paragraph> : null}
            </div>
          </div>

          <div className="landing-public__form-wrapper">
            <Title level={4} className="landing-public__form-title">Lasciaci i tuoi contatti</Title>

          <Form<LandingFormValues>
            form={form}
            layout="vertical"
            onFinish={buildSubmitHandler(successMessage)}
            onValuesChange={onFormValuesChange}
            requiredMark={false}
            className="landing-public__form-structure"
          >
            <Form.Item name="firstName">
              <Input placeholder="Mario" autoComplete="given-name" aria-label="Nome" />
            </Form.Item>
            <Form.Item name="lastName">
                <Input placeholder="Rossi" autoComplete="family-name" aria-label="Cognome" />
              </Form.Item>
              <Form.Item
                name="email"
                rules={emailRules}
                validateStatus={emailValidateStatus}
                help={emailHelp}
                validateTrigger={["onChange", "onBlur"]}
                validateDebounce={300}
              >
                <Input
                  placeholder="mario.rossi@example.com"
                  autoComplete="email"
                  aria-label="Email"
                  ref={emailInputRef}
                  onFocus={handleEmailFocus}
                  onBlur={handleEmailBlur}
                  suffix={emailSuffix ?? undefined}
                />
              </Form.Item>
              <Form.Item name="phoneNumber">
                <Input placeholder="+39 333 1234567" autoComplete="tel" aria-label="Telefono" />
              </Form.Item>
            <Form.Item
              name="privacyConsent"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error("Per proseguire \u00E8 necessario accettare l'informativa privacy.")),
                },
              ]}
              className="landing-public__privacy-item"
            >
              <Checkbox className="landing-public__privacy-consent">
                <Text type="secondary">Accetto l'<a className="landing-public__privacy-link" href="/privacy" target="_blank" rel="noopener noreferrer">informativa privacy</a></Text>
              </Checkbox>
            </Form.Item>
            <Form.Item className="landing-public__submit-item">
              <Button htmlType="submit" type="primary" block style={primaryButtonStyle}>
                {landing.buttonLabel?.trim() || "Invia richiesta"}
              </Button>
            </Form.Item>
          </Form>

          </div>
        </div>
        {renderFooter({ flush: true })}
      </div>
    );
  }

  return (
    <div className="landing-public landing-public--custom" style={customBackgroundStyle}>
      <div className="landing-public__page" style={pageStyle}>
        {positionedBlocks.map((block) => (
          <div
            key={block.id}
            className={`landing-public__block landing-public__block--${block.type}`}
            style={block.style}
          >
            {renderBlockContent(block)}
          </div>
        ))}
        {renderFooter()}
      </div>
    </div>
  );
};

export default LandingPublic;



